import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import {
  Save, Loader2, Eye, EyeOff, Image, Type, Link, Mail, 
  Calendar, Globe, AlertTriangle, ExternalLink, Upload, Palette
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminComingSoon = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [settings, setSettings] = useState({
    enabled: false,
    headline: 'Coming Soon',
    subheadline: "We're working on something exciting. Stay tuned!",
    description: '',
    background_image: '',
    background_overlay_opacity: 0.7,
    cta_enabled: true,
    cta_text: 'Get Notified',
    cta_type: 'email',
    cta_link: '',
    cta_email_placeholder: 'Enter your email',
    show_logo: true,
    show_social_links: false,
    social_links: {
      twitter: '',
      linkedin: '',
      facebook: ''
    },
    countdown_enabled: false,
    countdown_date: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, [navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/content/coming-soon`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error('Failed to fetch coming soon settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/content/coming-soon`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success('Coming soon settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, background_image: data.url }));
        toast.success('Background image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-[#00457f]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#00457f]" />
              Coming Soon Mode
            </h1>
            <p className="text-gray-600 mt-1">
              Enable maintenance mode and display a custom coming soon page
            </p>
          </div>
          <div className="flex items-center gap-3">
            {settings.enabled && (
              <a
                href="/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
              >
                <ExternalLink className="w-4 h-4" />
                Preview
              </a>
            )}
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#00457f] hover:bg-[#003366]"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save Changes
            </Button>
          </div>
        </div>

        {/* Enable/Disable Toggle */}
        <Card className={settings.enabled ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {settings.enabled ? (
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                ) : (
                  <Eye className="w-6 h-6 text-green-600" />
                )}
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {settings.enabled ? 'Coming Soon Mode is ACTIVE' : 'Website is Live'}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {settings.enabled 
                      ? 'Visitors will see the coming soon page instead of the main website'
                      : 'Visitors can access the full website normally'
                    }
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.enabled}
                onCheckedChange={(checked) => updateSetting('enabled', checked)}
              />
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Content Settings */}
          <div className="space-y-6">
            {/* Text Content */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Type className="w-5 h-5 text-[#00457f]" />
                  Content
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="headline">Headline</Label>
                  <Input
                    id="headline"
                    value={settings.headline}
                    onChange={(e) => updateSetting('headline', e.target.value)}
                    placeholder="Coming Soon"
                  />
                </div>
                <div>
                  <Label htmlFor="subheadline">Subheadline</Label>
                  <Input
                    id="subheadline"
                    value={settings.subheadline}
                    onChange={(e) => updateSetting('subheadline', e.target.value)}
                    placeholder="We're working on something exciting..."
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description (optional)</Label>
                  <Textarea
                    id="description"
                    value={settings.description}
                    onChange={(e) => updateSetting('description', e.target.value)}
                    placeholder="Additional details about when the site will be available..."
                    rows={3}
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label htmlFor="show_logo">Show Logo</Label>
                  <Switch
                    id="show_logo"
                    checked={settings.show_logo}
                    onCheckedChange={(checked) => updateSetting('show_logo', checked)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Background Image */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Image className="w-5 h-5 text-[#00457f]" />
                  Background
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Background Image</Label>
                  <div className="mt-2 space-y-3">
                    {settings.background_image && (
                      <div className="relative rounded-lg overflow-hidden h-32">
                        <img 
                          src={settings.background_image} 
                          alt="Background preview"
                          className="w-full h-full object-cover"
                        />
                        <button
                          onClick={() => updateSetting('background_image', '')}
                          className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full hover:bg-red-600"
                        >
                          <EyeOff className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <Input
                        value={settings.background_image}
                        onChange={(e) => updateSetting('background_image', e.target.value)}
                        placeholder="Enter image URL or upload"
                        className="flex-1"
                      />
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        <Button variant="outline" disabled={uploading} asChild>
                          <span>
                            {uploading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>
                <div>
                  <Label>Overlay Darkness: {Math.round(settings.background_overlay_opacity * 100)}%</Label>
                  <Slider
                    value={[settings.background_overlay_opacity]}
                    onValueChange={([value]) => updateSetting('background_overlay_opacity', value)}
                    min={0}
                    max={1}
                    step={0.1}
                    className="mt-2"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Higher values make text more readable
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Call to Action */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Link className="w-5 h-5 text-[#00457f]" />
                  Call to Action
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label htmlFor="cta_enabled">Enable CTA Button</Label>
                  <Switch
                    id="cta_enabled"
                    checked={settings.cta_enabled}
                    onCheckedChange={(checked) => updateSetting('cta_enabled', checked)}
                  />
                </div>
                
                {settings.cta_enabled && (
                  <>
                    <div>
                      <Label>CTA Type</Label>
                      <div className="flex gap-2 mt-2">
                        {['email', 'link'].map((type) => (
                          <button
                            key={type}
                            onClick={() => updateSetting('cta_type', type)}
                            className={`flex-1 py-2 px-4 rounded-lg border text-sm font-medium transition-colors ${
                              settings.cta_type === type
                                ? 'bg-[#00457f] text-white border-[#00457f]'
                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {type === 'email' ? (
                              <span className="flex items-center justify-center gap-2">
                                <Mail className="w-4 h-4" /> Email Signup
                              </span>
                            ) : (
                              <span className="flex items-center justify-center gap-2">
                                <Link className="w-4 h-4" /> External Link
                              </span>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="cta_text">Button Text</Label>
                      <Input
                        id="cta_text"
                        value={settings.cta_text}
                        onChange={(e) => updateSetting('cta_text', e.target.value)}
                        placeholder="Get Notified"
                      />
                    </div>
                    
                    {settings.cta_type === 'email' && (
                      <div>
                        <Label htmlFor="cta_email_placeholder">Email Placeholder</Label>
                        <Input
                          id="cta_email_placeholder"
                          value={settings.cta_email_placeholder}
                          onChange={(e) => updateSetting('cta_email_placeholder', e.target.value)}
                          placeholder="Enter your email"
                        />
                      </div>
                    )}
                    
                    {settings.cta_type === 'link' && (
                      <div>
                        <Label htmlFor="cta_link">Link URL</Label>
                        <Input
                          id="cta_link"
                          value={settings.cta_link}
                          onChange={(e) => updateSetting('cta_link', e.target.value)}
                          placeholder="https://..."
                        />
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Countdown */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-[#00457f]" />
                  Countdown Timer
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <Label htmlFor="countdown_enabled">Enable Countdown</Label>
                  <Switch
                    id="countdown_enabled"
                    checked={settings.countdown_enabled}
                    onCheckedChange={(checked) => updateSetting('countdown_enabled', checked)}
                  />
                </div>
                
                {settings.countdown_enabled && (
                  <div>
                    <Label htmlFor="countdown_date">Launch Date</Label>
                    <Input
                      id="countdown_date"
                      type="datetime-local"
                      value={settings.countdown_date}
                      onChange={(e) => updateSetting('countdown_date', e.target.value)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Preview */}
          <div className="lg:sticky lg:top-6 h-fit">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5 text-[#00457f]" />
                  Preview
                </CardTitle>
                <CardDescription>
                  How your coming soon page will appear to visitors
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div 
                  className="relative rounded-lg overflow-hidden h-[500px] flex items-center justify-center"
                  style={{
                    backgroundImage: settings.background_image ? `url(${settings.background_image})` : 'linear-gradient(135deg, #001428 0%, #00457f 100%)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {/* Overlay */}
                  <div 
                    className="absolute inset-0 bg-black"
                    style={{ opacity: settings.background_overlay_opacity }}
                  />
                  
                  {/* Content */}
                  <div className="relative z-10 text-center px-6 max-w-md">
                    {settings.show_logo && (
                      <img 
                        src="/assets/images/wnc-logo.png" 
                        alt="Logo"
                        className="h-16 mx-auto mb-6"
                      />
                    )}
                    
                    <h1 className="text-3xl font-bold text-white mb-3">
                      {settings.headline || 'Coming Soon'}
                    </h1>
                    
                    {settings.subheadline && (
                      <p className="text-lg text-yellow-400 mb-4">
                        {settings.subheadline}
                      </p>
                    )}
                    
                    {settings.description && (
                      <p className="text-gray-300 text-sm mb-6">
                        {settings.description}
                      </p>
                    )}
                    
                    {settings.cta_enabled && (
                      <div className="space-y-3">
                        {settings.cta_type === 'email' ? (
                          <div className="flex gap-2">
                            <input
                              type="email"
                              placeholder={settings.cta_email_placeholder}
                              className="flex-1 px-4 py-2 rounded-lg text-sm"
                              disabled
                            />
                            <button className="bg-yellow-500 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                              {settings.cta_text}
                            </button>
                          </div>
                        ) : (
                          <button className="bg-yellow-500 text-white px-6 py-2 rounded-lg text-sm font-semibold">
                            {settings.cta_text}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminComingSoon;
